import { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";

export default function PPRuleTemplateSelectionModal({ visible, close, onSelect }) {
     const [rules, setRules] = useState([]);
     const [selectedRule, setSelectedRule] = useState(null);
     const [searchKeyword, setSearchKeyword] = useState("");
     const [isLoading, setIsLoading] = useState(false);

     const auth = PPUtils.getLogin();
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     useEffect(() => {
          if (visible) {
               fetchRules();
          }
     }, [visible]);

     const fetchRules = async () => {
          setIsLoading(true);
          try {
               const allRules = await RuleEngineBackend.getAllRules(customerId, token);
               const sortedRules = (allRules || [])
                    .filter((rule) => !rule.isBackupRule)
                    .sort((a, b) => {
                         const channelCompare = (a.channel?.channelName || "").localeCompare(b.channel?.channelName || "");
                         if (channelCompare !== 0) return channelCompare;
                         return (a.rulePriority || 0) - (b.rulePriority || 0);
                    });
               setRules(sortedRules);
          } catch (error) {
               console.error("Error fetching rules for template selection:", error);
               setRules([]);
          } finally {
               setIsLoading(false);
          }
     };

     const filteredRules = useMemo(() => {
          const keyword = searchKeyword.trim().toLowerCase();
          if (!keyword) return rules;

          return rules.filter((rule) => {
               const idMatch = String(rule.id).includes(keyword);
               const nameMatch = (rule.name || "").toLowerCase().includes(keyword);
               const descriptionMatch = (rule.description || "").toLowerCase().includes(keyword);
               const channelMatch = (rule.channel?.channelName || "").toLowerCase().includes(keyword);
               const groupMatch = (rule.ruleGroup?.groupName || "").toLowerCase().includes(keyword);
               return idMatch || nameMatch || descriptionMatch || channelMatch || groupMatch;
          });
     }, [rules, searchKeyword]);

     const handleConfirm = () => {
          if (onSelect) {
               onSelect(selectedRule);
          }
          handleClose();
     };

     const handleClose = () => {
          setSelectedRule(null);
          setSearchKeyword("");
          close();
     };

     return (
          <Dialog
               header={
                    <span className="d-flex align-items-center">
                         <i className="fas fa-copy me-2"></i>{' '}Regel als Vorlage auswählen
                    </span>
               }
               visible={visible}
               style={{ width: "50rem" }}
               onHide={handleClose}
               dismissableMask
               footer={
                    <div className="d-flex justify-content-end gap-2">
                         <button type="button" className="btn btn-secondary" onClick={handleClose}>
                              Abbrechen
                         </button>
                         <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => {
                                   onSelect?.(null);
                                   handleClose();
                              }}
                              disabled={isLoading}>
                              Ohne Vorlage fortfahren
                         </button>
                         <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleConfirm}
                              disabled={isLoading || !selectedRule}>
                              <i className="fas fa-check me-2"></i>{' '}Vorlage übernehmen
                         </button>
                    </div>
               }>
               <p className="text-muted mb-3">
                    Wählen Sie eine bestehende Regel, deren Einstellungen als Vorlage für die neue Regel übernommen werden sollen.
               </p>

               {isLoading ? (
                    <div className="text-center py-4">
                         <output className="spinner-border mb-3">
                              <span className="visually-hidden">Loading...</span>
                         </output>
                         <div>Regeln werden geladen...</div>
                    </div>
               ) : rules.length === 0 ? (
                    <div className="text-center py-4">
                         <i className="fas fa-exclamation-triangle fs-1 text-warning mb-3"></i>
                         <h6>Keine Regeln verfügbar</h6>
                         <p className="text-muted">Es sind keine Regeln vorhanden, die als Vorlage verwendet werden können.</p>
                    </div>
               ) : (
                    <>
                         <input
                              type="text"
                              className="form-control mb-3"
                              placeholder="Nach ID, Name, Beschreibung, Kanal oder Gruppe suchen..."
                              value={searchKeyword}
                              onChange={(e) => setSearchKeyword(e.target.value)}
                         />

                         {filteredRules.length === 0 ? (
                              <div className="text-center text-muted py-3">
                                   Keine Regeln für die Suche gefunden.
                              </div>
                         ) : (
                              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                   {filteredRules.map((rule) => (
                                        <div key={rule.id} className="mb-2">
                                             <button
                                                  type="button"
                                                  className={`card h-100 pp-rule-selection-card ${selectedRule?.id === rule.id ? "border-primary border-3 pp-rule-selection-card--selected" : "border-secondary"}`}
                                                  style={{
                                                       cursor: "pointer",
                                                       backgroundColor: selectedRule?.id === rule.id ? "#e3f2fd" : "white",
                                                       textAlign: "left",
                                                       width: "100%",
                                                       padding: 0
                                                  }}
                                                  onClick={() => {
                                                       setSelectedRule(selectedRule?.id === rule.id ? null : rule);
                                                  }}>
                                                  <div className="card-body py-2">
                                                       <div className="d-flex align-items-center justify-content-between">
                                                            <div className="flex-grow-1">
                                                                 <h6 className="card-title mb-1 text-dark">
                                                                      {rule.name || "Unbenannte Regel"}
                                                                      <small className="text-muted ms-2">(ID: {rule.id})</small>
                                                                 </h6>
                                                                 {rule.description && (
                                                                      <p className="card-text text-muted mb-1 small">
                                                                           {rule.description}
                                                                      </p>
                                                                 )}
                                                                 <small className="text-muted">
                                                                      {rule.channel?.channelName || "—"}
                                                                      {rule.ruleGroup?.groupName ? ` · ${rule.ruleGroup.groupName}` : ""}
                                                                      {rule.rulePriority ? ` · Priorität ${rule.rulePriority}` : ""}
                                                                 </small>
                                                            </div>
                                                            {selectedRule?.id === rule.id && (
                                                                 <div className="ms-3">
                                                                      <i className="fas fa-check-circle text-primary fs-4"></i>
                                                                 </div>
                                                            )}
                                                       </div>
                                                  </div>
                                             </button>
                                        </div>
                                   ))}
                              </div>
                         )}
                    </>
               )}
          </Dialog>
     );
}
